import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const LeadershipDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsResponse, employeesResponse] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/employees'),
      ]);
      setDashboardStats(statsResponse);
      setEmployees(employeesResponse);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Dashboard data fetch failed:', err);
    }
    setLoading(false);
  };

  const getStats = () => {
    const completed = employees.filter((e) => e.status === 'Completed').length;
    const pending = employees.filter((e) => e.status === 'Pending').length;
    const totalWithRating = employees.filter((e) => e.rating);
    const avgRating =
      totalWithRating.length > 0
        ? (
            totalWithRating.reduce((sum, e) => sum + e.rating, 0) /
            totalWithRating.length
          ).toFixed(2)
        : 0;

    return {
      total: employees.length,
      completed,
      pending,
      avgRating,
    };
  };

  const getDepartmentStats = () => {
    const depts = {};
    employees.forEach((emp) => {
      const cat = emp.category || 'Other';
      if (!depts[cat]) {
        depts[cat] = { count: 0, totalRating: 0, ratedCount: 0, totalSalary: 0 };
      }
      depts[cat].count++;
      depts[cat].totalSalary += (emp.salary || 0);
      if (emp.rating) {
        depts[cat].totalRating += emp.rating;
        depts[cat].ratedCount++;
      }
    });

    return Object.entries(depts).map(([dept, data]) => ({
      department: dept,
      count: data.count,
      totalSalary: data.totalSalary,
      avgRating:
        data.ratedCount > 0 ? (data.totalRating / data.ratedCount).toFixed(2) : 0,
      performance:
        data.ratedCount > 0 && data.totalRating / data.ratedCount >= 3.5
          ? 'Strong'
          : 'Needs Improvement',
    }));
  };

  const getTotalSalaryData = () => {
    const totalMonthlyCTC = employees.reduce((sum, emp) => sum + (emp.salary || 0), 0);
    const baseSalary = totalMonthlyCTC * 12; // annual payroll
    const proposedIncrement = (baseSalary * 8) / 100; // 8% average increment
    return {
      baseSalary,
      proposedIncrement,
      newTotal: baseSalary + proposedIncrement,
    };
  };

  const stats = getStats();
  const deptStats = getDepartmentStats();
  const salaryData = getTotalSalaryData();

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '20px',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
    },
    cardTitle: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#999',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '12px',
    },
    cardValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1a1a2e',
      lineHeight: '1.2',
    },
    cardSubtext: {
      fontSize: '12px',
      color: '#bbb',
      marginTop: '8px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1a1a2e',
      marginBottom: '20px',
      marginTop: '32px',
    },
    departmentGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '16px',
    },
    deptCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    deptName: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#1a1a2e',
      marginBottom: '12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    deptStat: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: '8px',
      borderBottom: '1px solid #f0f0f0',
      marginBottom: '8px',
      fontSize: '13px',
    },
    deptStatLabel: {
      color: '#666',
    },
    deptStatValue: {
      fontWeight: '700',
      color: '#1a1a2e',
    },
    performanceBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '12px',
    },
    tableHeader: {
      backgroundColor: '#f9f9f9',
      borderBottom: '2px solid #e0e0e0',
    },
    tableHeaderCell: {
      padding: '12px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '700',
      color: '#1a1a2e',
      textTransform: 'uppercase',
    },
    tableRow: {
      borderBottom: '1px solid #f0f0f0',
    },
    tableCell: {
      padding: '12px',
      fontSize: '13px',
      color: '#666',
    },
    heatmapContainer: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    heatmapGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '12px',
      marginTop: '16px',
    },
    heatmapCell: {
      padding: '16px',
      borderRadius: '8px',
      textAlign: 'center',
      color: 'white',
      fontWeight: '700',
    },
  };

  const getPerformanceColor = (rating) => {
    if (rating >= 4.0) return { bg: '#8bc34a', label: 'Strong' };
    if (rating >= 3.5) return { bg: '#ffc107', label: 'Adequate' };
    if (rating >= 3.0) return { bg: '#ff9800', label: 'Average' };
    return { bg: '#f44336', label: 'Below' };
  };

  // Show error state if API fails
  if (error) {
    return (
      <div style={styles.container}>
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #ef5350',
            color: '#c62828',
            padding: '20px',
            borderRadius: '12px',
            fontSize: '14px',
          }}
        >
          <strong>Error loading dashboard:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.statsGrid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Total Employees</div>
          <div style={styles.cardValue}>{stats.total}</div>
          <div style={styles.cardSubtext}>Across all departments</div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Completion Rate</div>
          <div style={styles.cardValue}>
            {stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(0) : 0}%
          </div>
          <div style={styles.cardSubtext}>
            {stats.completed} of {stats.total} completed
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Avg Organization Rating</div>
          <div style={styles.cardValue}>{stats.avgRating}</div>
          <div style={styles.cardSubtext}>Weighted average across all rated employees</div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Pending Reviews</div>
          <div style={styles.cardValue}>{stats.pending}</div>
          <div style={styles.cardSubtext}>Awaiting appraisal completion</div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Salary & Budget Overview</div>
      <div style={styles.statsGrid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Total Current Payroll</div>
          <div style={styles.cardValue}>
            ₹ {(salaryData.baseSalary / 1000000).toFixed(1)}M
          </div>
          <div style={styles.cardSubtext}>
            ₹ {salaryData.baseSalary.toLocaleString('en-IN')}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Proposed Increment Budget</div>
          <div style={styles.cardValue} style={{ color: '#8bc34a' }}>
            ₹ {(salaryData.proposedIncrement / 100000).toFixed(1)}L
          </div>
          <div style={styles.cardSubtext}>
            ~{((salaryData.proposedIncrement / salaryData.baseSalary) * 100).toFixed(1)}% of current payroll
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>New Total Payroll</div>
          <div style={styles.cardValue}>
            ₹ {(salaryData.newTotal / 1000000).toFixed(1)}M
          </div>
          <div style={styles.cardSubtext}>
            ₹ {salaryData.newTotal.toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Department-wise Performance</div>
      <div style={styles.departmentGrid}>
        {deptStats.map((dept) => {
          const perfColor = getPerformanceColor(dept.avgRating);
          return (
            <div key={dept.department} style={styles.deptCard}>
              <div style={styles.deptName}>
                <span>{dept.department}</span>
                <span style={{ fontSize: '12px', color: '#999', fontWeight: '600' }}>
                  {dept.count} emp.
                </span>
              </div>

              <div style={styles.deptStat}>
                <span style={styles.deptStatLabel}>Avg Rating</span>
                <span style={styles.deptStatValue}>{dept.avgRating}</span>
              </div>

              <div style={styles.deptStat} style={{ borderBottom: 'none', marginBottom: '12px' }}>
                <span style={styles.deptStatLabel}>Status</span>
                <div
                  style={{
                    ...styles.performanceBadge,
                    backgroundColor: perfColor.bg,
                    opacity: 0.2,
                    color: perfColor.bg,
                  }}
                >
                  {perfColor.label}
                </div>
              </div>

              <div
                style={{
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: '#e0e0e0',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    backgroundColor: perfColor.bg,
                    width: `${(dept.avgRating / 5) * 100}%`,
                    transition: 'width 0.3s ease',
                  }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.sectionTitle}>Top Performers</div>
      <div style={styles.heatmapContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Name</th>
              <th style={styles.tableHeaderCell}>Designation</th>
              <th style={styles.tableHeaderCell}>Department</th>
              <th style={styles.tableHeaderCell}>Rating</th>
              <th style={styles.tableHeaderCell}>Performance</th>
            </tr>
          </thead>
          <tbody>
            {employees
              .filter((e) => e.rating)
              .sort((a, b) => (b.rating || 0) - (a.rating || 0))
              .slice(0, 5)
              .map((emp, idx) => {
                const perfColor = getPerformanceColor(emp.rating);
                return (
                  <tr key={emp.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#1a1a2e' }}>
                        {emp.name}
                      </div>
                    </td>
                    <td style={styles.tableCell}>{emp.designation}</td>
                    <td style={styles.tableCell}>{emp.category}</td>
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          fontWeight: '700',
                          color: perfColor.bg,
                        }}
                      >
                        {emp.rating.toFixed(1)}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          ...styles.performanceBadge,
                          backgroundColor: perfColor.bg,
                          opacity: 0.2,
                          color: perfColor.bg,
                        }}
                      >
                        {perfColor.label}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div style={styles.sectionTitle}>Performance Heatmap</div>
      <div style={styles.heatmapContainer}>
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
          Department ratings visualization
        </div>
        <div style={styles.heatmapGrid}>
          {deptStats.map((dept) => {
            const perfColor = getPerformanceColor(dept.avgRating);
            return (
              <div
                key={dept.department}
                style={{
                  ...styles.heatmapCell,
                  backgroundColor: perfColor.bg,
                }}
              >
                <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>
                  {dept.department}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700' }}>
                  {dept.avgRating}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LeadershipDashboard;
